import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { useTranslation } from "../../../utils/translation";
import {
  listBusinesses,
  selectBusinesses,
  selectBusinessesError,
  selectBusinessesLoading,
  selectBusinessesMessage,
  selectBusinessesUpdateError,
  selectBusinessesUpdateMessage,
  updateBusinessById,
} from "../businessSlice";

const PAGE_SIZE = 10;

const BusinessEditPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const businesses = useSelector(selectBusinesses);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const isLoading = useSelector(selectBusinessesLoading);
  const isError = useSelector(selectBusinessesError);
  const message = useSelector(selectBusinessesMessage);
  const updateError = useSelector(selectBusinessesUpdateError);
  const updateMessage = useSelector(selectBusinessesUpdateMessage);
  const [permissionError, setPermissionError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showEdit, setShowEdit] = useState(false);
  const [editBusiness, setEditBusiness] = useState(null);
  const [formState, setFormState] = useState({
    businessName: "",
    businessMail: "",
    businessState: true,
  });

  useEffect(() => {
    dispatch(listBusinesses());
  }, [dispatch]);

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>{match}</span>
        {after}
      </>
    );
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return businesses || [];
    return (businesses || []).filter((biz) =>
      (biz?.businessName || "").toLowerCase().includes(term)
    );
  }, [businesses, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const openEditModal = (biz) => {
    setEditBusiness(biz);
    setFormState({
      businessName: biz?.businessName || "",
      businessMail: biz?.businessMail || "",
      businessState: Boolean(biz?.businessState),
    });
    setShowEdit(true);
  };

  const closeEditModal = () => {
    setShowEdit(false);
    setEditBusiness(null);
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (!adminAccess) {
      setPermissionError(t("admin_common.permission_denied"));
      return;
    }
    if (!editBusiness?.id) return;
    setPermissionError("");
    const payload = {
      businessName: formState.businessName?.trim() || "",
      businessMail: formState.businessMail?.trim() || "",
      businessState: Boolean(formState.businessState),
    };
    try {
      await dispatch(updateBusinessById({ id: editBusiness.id, data: payload })).unwrap();
      await dispatch(listBusinesses());
      closeEditModal();
    } catch (error) {
      // Error handled in slice state
    }
  };

  const renderRows = () => {
    if (isLoading && (!businesses || businesses.length === 0)) {
      return (
        <tr>
          <td colSpan="3">{t("admin_common.loading")}</td>
        </tr>
      );
    }
    if (isError && (!businesses || businesses.length === 0)) {
      return (
        <tr>
          <td colSpan="3" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }
    if (!paged || paged.length === 0) {
      return (
        <tr>
          <td colSpan="3">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    return paged.map((biz) => (
      <tr key={biz.id || biz.businessId}>
        <td>{highlightMatch(biz?.businessName || "-", search.trim())}</td>
        <td>{biz?.businessMail || "-"}</td>
        <td>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => openEditModal(biz)}
            disabled={!biz?.id || !adminAccess}
          >
            {t("admin_common.edit")}
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{t("admin_business_pages.edit_title")}</h4>
              {permissionError && (
                <div className="alert alert-danger">{permissionError}</div>
              )}
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("admin_common.search_name_placeholder")}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>{t("admin_common.business")}</th>
                      <th>{t("admin_common.email")}</th>
                      <th>{t("admin_common.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-3">
                <span className="text-muted">
                  {t("admin_common.total_records", { count: filtered.length })}
                </span>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("admin_common.prev")}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled>
                    {currentPage} / {totalPages}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t("admin_common.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal className="modal fade" show={showEdit} onHide={closeEditModal}>
        <div role="document">
          <div>
            <form>
              <div className="modal-header">
                <h4 className="modal-title fs-20">{t("admin_business_pages.edit_title")}</h4>
                <button type="button" className="btn-close" onClick={closeEditModal}>
                  <span></span>
                </button>
              </div>
              <div className="modal-body">
                {updateError && (
                  <div className="text-danger mb-2">
                    {updateMessage || t("admin_business_pages.update_failed")}
                  </div>
                )}
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_business_pages.name_label")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="businessName"
                    value={formState.businessName}
                    onChange={handleFormChange}
                    placeholder={t("admin_business_pages.name_label")}
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_business_pages.email_label")}</label>
                  <input
                    type="email"
                    className="form-control"
                    name="businessMail"
                    value={formState.businessMail}
                    onChange={handleFormChange}
                    placeholder={t("admin_business_pages.email_label")}
                  />
                </div>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="business-edit-active"
                    name="businessState"
                    checked={Boolean(formState.businessState)}
                    onChange={handleFormChange}
                  />
                  <label className="form-check-label" htmlFor="business-edit-active">
                    {formState.businessState ? t("admin_common.status_active") : t("admin_common.status_passive")}
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={closeEditModal}>
                  {t("admin_common.cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={!editBusiness?.id}
                >
                  {t("admin_common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BusinessEditPage;
